/**
 * Cart behaviour (drawer + cart page). Loaded with defer.
 *
 * <cart-form data-section-id>: AJAX cart for line items rendered by
 *   snippets/cart-line-items.liquid. Quantity steppers / inputs / remove
 *   links call /cart/change.js with `sections` and swap in the re-rendered
 *   [data-cart-form] region (Section Rendering API, no reloads). Handles
 *   quantity limit messages, order note autosave, one-click add forms
 *   ([data-cart-add]) and keeps focus in place after every re-render.
 *   Dispatches `cart:updated` (header count, free-shipping bars).
 *
 * <cart-drawer>: modal <dialog> (right panel / mobile bottom sheet) with
 *   focus trap, Esc + backdrop close, exit animation. Opens on header
 *   [data-cart-drawer-trigger] clicks (the link still goes to /cart without
 *   JS) and after every `cart:item-added` (product page, quick add, bundle).
 *   Disabled on the cart page ([data-disabled]); the toast then takes over.
 *
 * <cart-upsell>: "Complete the set" rows from
 *   /recommendations/products.json?intent=complementary (fallback: related).
 */
(() => {
  if (customElements.get('cart-form')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const money = (cents) => (window.theme && window.theme.formatMoney ? window.theme.formatMoney(cents) : String(cents));
  const motionOff = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    getComputedStyle(document.documentElement).getPropertyValue('--motion-scale').trim() === '0';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  let selfDispatch = false;
  const emit = (cart) => {
    selfDispatch = true;
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } finally {
      selfDispatch = false;
    }
  };

  const request = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.description || data.message || '');
      error.status = response.status;
      throw error;
    }
    return data;
  };
  const post = (path, body) => request(`${root}${path}`, { method: 'POST', body: JSON.stringify(body) });
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Fresh cart (no-store + cache-buster); theme.js owns the shared helper.
  const fetchCart = () =>
    window.theme && window.theme.fetchCart
      ? window.theme.fetchCart()
      : request(`${root}cart.js?_=${Date.now()}`, { cache: 'no-store' });

  // Section Rendering API (GET). Deliberately WITHOUT `Accept: application/json`:
  // with that header Shopify answers /products/*, /cart and /pages/* with the
  // resource JSON instead of the sections payload (the drawer then stayed empty).
  const fetchSection = async (path, id) => {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('sections', id);
    url.searchParams.set('_', String(Date.now()));
    const response = await fetch(url.toString(), { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error(String(response.status));
    const data = await response.json();
    return data && typeof data[id] === 'string' ? data[id] : null;
  };

  /* ---------------------------------------------------------------------- */
  /* <cart-form>                                                            */
  /* ---------------------------------------------------------------------- */
  class CartForm extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.queue = Promise.resolve();
      this.timers = new Map();
      this.stale = false;

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('input', (event) => this.onInput(event));

      this.onExternal = () => {
        if (selfDispatch) return;
        this.stale = true;
        if (this.hasAttribute('data-auto-refresh')) this.refresh();
      };
      document.addEventListener('cart:updated', this.onExternal);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onExternal);
    }

    get live() {
      const scope = this.closest('[data-cart-scope]') || this.parentElement;
      return scope ? scope.querySelector('[data-cart-live]') : null;
    }

    announce(message) {
      const live = this.live;
      if (!live || !message) return;
      live.textContent = '';
      requestAnimationFrame(() => {
        live.textContent = message;
      });
    }

    lineOf(el) {
      return el.closest('[data-line]');
    }

    limits(input) {
      const step = Number(input.step) || 1;
      const max = input.max === '' ? Infinity : Number(input.max);
      return { step, max };
    }

    /* --- events --- */
    onClick(event) {
      const stepper = event.target.closest('[data-qty-step]');
      if (stepper) {
        event.preventDefault();
        const line = this.lineOf(stepper);
        const input = line.querySelector('[data-qty-input]');
        const { step, max } = this.limits(input);
        const direction = Number(stepper.dataset.qtyStep);
        if (direction > 0 && stepper.getAttribute('aria-disabled') === 'true') {
          this.lineError(line, this.dataset.textMax);
          return;
        }
        let next = (Number(input.value) || 0) + direction * step;
        if (next > max) {
          this.lineError(line, this.dataset.textMax);
          next = max;
        }
        next = Math.max(next, 0);
        input.value = next;
        this.schedule(line, next, stepper);
        return;
      }

      const remove = event.target.closest('[data-line-remove]');
      if (remove) {
        event.preventDefault();
        this.schedule(this.lineOf(remove), 0, remove, 0);
      }
    }

    onChange(event) {
      const input = event.target.closest('[data-qty-input]');
      if (input) {
        const line = this.lineOf(input);
        const { max } = this.limits(input);
        let value = parseInt(input.value, 10);
        if (Number.isNaN(value) || value < 0) value = 0;
        if (value > max) {
          value = max;
          this.lineError(line, this.dataset.textMax);
        }
        input.value = value;
        if (value !== Number(line.dataset.quantity)) this.schedule(line, value, input, 0);
        return;
      }

      const select = event.target.closest('.mini-product__select');
      if (select) {
        const option = select.selectedOptions[0];
        const price = select.closest('.mini-product').querySelector('[data-u-price]');
        if (option && price && option.dataset.price) price.textContent = option.dataset.price;
      }
    }

    onKeydown(event) {
      // Enter in a quantity field must not submit the form (that would check out).
      if (event.key === 'Enter' && event.target.matches('[data-qty-input]')) {
        event.preventDefault();
        event.target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (form.matches('[data-cart-add]')) {
        event.preventDefault();
        this.add(form, event.submitter || form.querySelector('[type="submit"]'));
        return;
      }
      // Checkout: flush a pending note so it is saved with the order.
      const note = this.querySelector('[data-cart-note]');
      if (note && this.noteTimer) {
        clearTimeout(this.noteTimer);
        this.noteTimer = null;
      }
    }

    onInput(event) {
      const note = event.target.closest('[data-cart-note]');
      if (!note) return;
      clearTimeout(this.noteTimer);
      const details = note.closest('details');
      if (details) details.classList.remove('is-saved');
      this.noteTimer = setTimeout(async () => {
        this.noteTimer = null;
        try {
          await post('cart/update.js', { note: note.value });
          if (details) details.classList.add('is-saved');
          this.announce(this.dataset.textNoteSaved);
        } catch (error) {
          this.announce(this.dataset.textError);
        }
      }, 600);
    }

    /* --- cart operations --- */
    schedule(line, quantity, trigger, delay = 320) {
      const key = line.dataset.key;
      this.clearLineError(line);
      clearTimeout(this.timers.get(key));
      this.timers.set(
        key,
        setTimeout(() => {
          this.timers.delete(key);
          this.queue = this.queue.then(() => this.change(key, quantity, this.describeFocus(trigger)));
        }, delay)
      );
    }

    describeFocus(el) {
      if (!el || !this.contains(document.activeElement)) return null;
      const line = this.lineOf(el);
      let role = 'input';
      if (el.matches('[data-qty-step="1"]')) role = 'plus';
      else if (el.matches('[data-qty-step="-1"]')) role = 'minus';
      else if (el.matches('[data-line-remove]')) role = 'remove';
      return { key: line ? line.dataset.key : null, index: line ? Number(line.dataset.line) : 1, role };
    }

    async change(key, quantity, focus) {
      const line = this.querySelector(`[data-line][data-key="${CSS.escape(key)}"]`);
      if (!line) return;
      const previous = Number(line.dataset.quantity);
      if (quantity === previous) return;
      line.classList.add('is-updating');
      this.setAttribute('aria-busy', 'true');

      try {
        const cart = await post('cart/change.js', {
          id: key,
          quantity,
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        });
        if (!this.render(cart.sections && cart.sections[this.sectionId], focus)) await this.refresh(cart);
        emit(cart);

        const item = quantity > 0 ? cart.items.find((entry) => entry.key === key) : null;
        if (item && item.quantity < quantity) {
          const fresh = this.querySelector(`[data-line][data-key="${CSS.escape(key)}"]`);
          const message = (this.dataset.textQtyError || '').replace('[quantity]', item.quantity);
          if (fresh) this.lineError(fresh, message);
          this.announce(message);
        } else {
          this.announce(quantity === 0 ? this.dataset.textRemoved : this.dataset.textUpdated);
        }
      } catch (error) {
        line.classList.remove('is-updating');
        const input = line.querySelector('[data-qty-input]');
        if (input) input.value = previous;
        const message = error.message || this.dataset.textError;
        this.lineError(line, message);
        this.announce(message);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    async add(form, button) {
      if (!button || button.getAttribute('aria-busy') === 'true') return;
      const id = Number(new FormData(form).get('id'));
      if (!id) return;
      button.setAttribute('aria-busy', 'true');
      delete button.dataset.state;
      const card = form.closest('.mini-product');
      const oldError = card && card.querySelector('.mini-product__error');
      if (oldError) oldError.remove();

      try {
        const data = await post('cart/add.js', {
          items: [{ id, quantity: 1 }],
          sections: [this.sectionId],
          sections_url: window.location.pathname,
        });
        const added = data.items && data.items[0];
        button.removeAttribute('aria-busy');
        button.dataset.state = 'added';
        const cart = await fetchCart().catch(() => null);
        await wait(motionOff() ? 0 : 450);
        const focus = added ? { key: added.key, role: 'title' } : null;
        const addedHtml = data.sections && data.sections[this.sectionId];
        if (!(this.fits(addedHtml, cart) && this.render(addedHtml, focus))) await this.refresh(cart);
        if (cart) emit(cart);
        this.announce(`${this.dataset.textAdded || ''} ${added ? added.product_title : ''}`.trim());
        const fresh = added && this.querySelector(`[data-line][data-key="${CSS.escape(added.key)}"]`);
        if (fresh && !motionOff()) fresh.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 420, easing: 'cubic-bezier(0.33, 1, 0.68, 1)' });
      } catch (error) {
        button.removeAttribute('aria-busy');
        button.dataset.state = 'error';
        const message = error.message || this.dataset.textError;
        if (card) {
          const note = document.createElement('p');
          note.className = 'mini-product__error';
          note.setAttribute('role', 'alert');
          note.textContent = message;
          card.append(note);
        }
        setTimeout(() => delete button.dataset.state, 2400);
      }
    }

    /* Show `html` (sections payload from add.js) when it matches the cart,
       otherwise re-render. */
    async sync({ html = null, cart = null } = {}) {
      // Called right after an add: with no cart to compare, an empty payload is stale by definition.
      const plausible = cart || (html && html.includes('data-line='));
      if (plausible && this.fits(html, cart) && this.render(html, null)) {
        this.stale = false;
        return;
      }
      await this.refresh(cart);
    }

    /* Do the lines under `scope` match the cart (same line keys + quantities)? */
    matches(cart, scope = this) {
      if (!cart || !Array.isArray(cart.items)) return true;
      const signature = (pairs) => pairs.sort().join('|');
      const rendered = Array.from(scope.querySelectorAll('[data-line]')).map(
        (line) => `${line.dataset.key}:${line.dataset.quantity}`
      );
      return signature(rendered) === signature(cart.items.map((item) => `${item.key}:${item.quantity}`));
    }

    /* Checked BEFORE painting: a stale payload (e.g. the empty-cart state
       rendered before /cart/add.js committed) must never replace the drawer. */
    fits(html, cart) {
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`[data-cart-form="${this.sectionId}"]`);
      return Boolean(fresh) && this.matches(cart, fresh);
    }

    /* Re-render via the Section Rendering API: current page, then the home
       page (renders static sections such as the drawer), retried once with a
       fresh cart.js as the source of truth. Last resort: lines built from
       cart.js (drawer) or a reload (cart page). */
    async refresh(expected = null) {
      this.stale = false;
      this.setAttribute('aria-busy', 'true');
      try {
        if (!expected) expected = await fetchCart().catch(() => null);
        const paths = Array.from(new Set([window.location.pathname, root]));
        for (let attempt = 0; attempt < 2; attempt += 1) {
          if (attempt) {
            await wait(350);
            expected = await fetchCart().catch(() => expected);
          }
          for (const path of paths) {
            try {
              const html = await fetchSection(path, this.sectionId);
              if (this.fits(html, expected) && this.render(html, null)) return true;
            } catch (error) {
              /* try the next path */
            }
          }
        }
        const cart = await fetchCart().catch(() => expected);
        if (cart && this.renderFromCart(cart)) return true;
        this.stale = true;
        return false;
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    /* Fallback markup from /cart.js (drawer only; clones <template>s rendered
       by the section so strings, icons and classes match the Liquid version). */
    renderFromCart(cart) {
      const current = this.querySelector(`[data-cart-form="${this.sectionId}"]`);
      const shell = this.querySelector('template[data-cart-fallback]');
      const lineTemplate = this.querySelector('template[data-cart-line-template]');
      if (!current || !shell || !lineTemplate) {
        if (!this.closest('cart-drawer') && !this.reloaded) {
          this.reloaded = true;
          window.location.reload();
        }
        return false;
      }
      if (!cart.items || !cart.items.length) return false;

      const fragment = shell.content.cloneNode(true);
      const list = fragment.querySelector('[data-fallback-lines]');
      cart.items.forEach((item, index) => {
        const node = lineTemplate.content.firstElementChild.cloneNode(true);
        const title = item.product_title || item.title;
        node.dataset.line = String(index + 1);
        node.dataset.key = item.key;
        node.dataset.quantity = String(item.quantity);
        node.querySelectorAll('[data-f-link]').forEach((link) => link.setAttribute('href', item.url));
        node.querySelector('[data-f-title]').textContent = title;

        const media = node.querySelector('.cart-line__media');
        const src = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
        if (src && media) {
          const url = new URL(src, window.location.href);
          url.searchParams.set('width', '360');
          const img = new Image(360, 270);
          img.src = url.toString();
          img.alt = '';
          img.loading = 'lazy';
          media.append(img);
        }

        const meta = node.querySelector('[data-f-meta]');
        const options = (item.options_with_values || []).filter((option) => !/default title/i.test(option.value));
        if (meta && options.length && !item.product_has_only_default_variant) {
          meta.textContent = options.map((option) => `${option.name}: ${option.value}`).join(' · ');
          meta.hidden = false;
        }

        const original = item.original_line_price;
        const final = Math.min(item.final_line_price, original - (item.total_discount || 0));
        const was = node.querySelector('[data-f-original]');
        if (was && final < original) {
          was.textContent = money(original);
          was.hidden = false;
        }
        node.querySelector('[data-f-final]').textContent = money(final);

        const input = node.querySelector('[data-qty-input]');
        input.value = String(item.quantity);
        input.id = `Quantity-${this.sectionId}-f${index + 1}`;
        node.querySelectorAll('[aria-label]').forEach((el) =>
          el.setAttribute('aria-label', el.getAttribute('aria-label').replace('__title__', title))
        );
        node.querySelector('[data-line-remove]').setAttribute('href', `${root}cart/change?line=${index + 1}&quantity=0`);
        list.append(node);
      });

      const discounts = (cart.cart_level_discount_applications || []).filter((discount) => discount.total_allocated_amount > 0);
      const discountRow = fragment.querySelector('[data-fallback-discount]');
      if (discountRow && discounts.length) {
        discountRow.querySelector('[data-fallback-discount-title]').textContent = discounts.map((discount) => discount.title).join(', ');
        discountRow.querySelector('[data-fallback-discount-amount]').textContent = `−${money(
          discounts.reduce((sum, discount) => sum + discount.total_allocated_amount, 0)
        )}`;
        discountRow.hidden = false;
      }
      const was = fragment.querySelector('[data-fallback-original]');
      if (was && cart.original_total_price > cart.total_price) {
        was.textContent = money(cart.original_total_price);
        was.hidden = false;
      }
      fragment.querySelector('[data-fallback-total]').textContent = money(cart.total_price);

      current.replaceChildren(fragment);
      return true;
    }

    /* --- rendering --- */
    render(html, focus) {
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`[data-cart-form="${this.sectionId}"]`);
      const current = this.querySelector(`[data-cart-form="${this.sectionId}"]`);
      if (!fresh || !current) return false;

      const scroller = current.querySelector('[data-cart-scroll]');
      const scrollTop = scroller ? scroller.scrollTop : 0;
      const pageScroll = window.scrollY;
      const note = current.querySelector('[data-cart-note]');
      const noteState = note
        ? {
            value: note.value,
            focused: document.activeElement === note,
            start: note.selectionStart,
            end: note.selectionEnd,
            open: note.closest('details') ? note.closest('details').open : false,
          }
        : null;

      current.replaceChildren(...fresh.childNodes);

      const nextScroller = current.querySelector('[data-cart-scroll]');
      if (nextScroller) nextScroller.scrollTop = scrollTop;
      const nextNote = current.querySelector('[data-cart-note]');
      if (noteState && nextNote) {
        nextNote.value = noteState.value;
        const details = nextNote.closest('details');
        if (details) details.open = noteState.open || noteState.value !== '';
        if (noteState.focused) {
          nextNote.focus({ preventScroll: true });
          nextNote.setSelectionRange(noteState.start, noteState.end);
        }
      }
      this.restoreFocus(focus);
      if (Math.abs(window.scrollY - pageScroll) > 1) window.scrollTo({ top: pageScroll });
      if (window.theme && window.theme.initMotion) window.theme.initMotion(current);
      return true;
    }

    restoreFocus(focus) {
      if (!focus) return;
      const line = focus.key ? this.querySelector(`[data-line][data-key="${CSS.escape(focus.key)}"]`) : null;
      let target = null;
      if (line) {
        const map = {
          plus: '[data-qty-step="1"]',
          minus: '[data-qty-step="-1"]',
          input: '[data-qty-input]',
          remove: '[data-line-remove]',
          title: '.cart-line__title',
        };
        target = line.querySelector(map[focus.role] || '[data-qty-input]');
      } else {
        const lines = this.querySelectorAll('[data-line]');
        const near = lines[Math.min(focus.index, lines.length) - 1];
        target = near
          ? near.querySelector('.cart-line__title')
          : this.querySelector('.cart-empty a, .cart-empty button') ||
            document.querySelector('#CartDrawer-Title, #CartPage-Title');
      }
      if (target) target.focus({ preventScroll: true });
    }

    clearLineError(line) {
      const error = line && line.querySelector('[data-line-error]');
      if (error) error.hidden = true;
    }

    lineError(line, message) {
      const error = line && line.querySelector('[data-line-error]');
      if (!error || !message) return;
      error.textContent = message;
      error.hidden = false;
    }
  }
  customElements.define('cart-form', CartForm);

  /* ---------------------------------------------------------------------- */
  /* <cart-drawer>                                                          */
  /* ---------------------------------------------------------------------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('cart-form');
      if (!this.dialog || !this.form || this.hasAttribute('data-disabled')) return;

      // Tells the toast (theme.js) that the drawer handles add-to-cart feedback.
      document.documentElement.setAttribute('data-cart-drawer', '');
      this.dialog.setAttribute('data-cart-scope', '');

      this.triggers = () => document.querySelectorAll('[data-cart-drawer-trigger]');
      this.triggers().forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.setAttribute('aria-expanded', 'false');
      });

      this.onTrigger = (event) => {
        const trigger = event.target.closest('[data-cart-drawer-trigger]');
        if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(trigger);
      };
      document.addEventListener('click', this.onTrigger);

      this.onAdded = (event) => {
        const detail = event.detail || {};
        const html = detail.sections && detail.sections[this.form.sectionId];
        this.open(detail.source, { refresh: true, html, cart: detail.cart || null });
      };
      document.addEventListener('cart:item-added', this.onAdded);

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-drawer-close]')) this.close();
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));
      this.dialog.addEventListener('close', () => {
        this.dialog.classList.remove('is-closing');
        this.triggers().forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        const target = this.opener && this.opener.isConnected ? this.opener : document.querySelector('[data-cart-drawer-trigger]');
        if (target) target.focus({ preventScroll: true });
      });

      window.theme = Object.assign(window.theme || {}, { openCart: (opener) => this.open(opener, { refresh: true }) });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
      document.removeEventListener('cart:item-added', this.onAdded);
      document.documentElement.removeAttribute('data-cart-drawer');
    }

    open(opener, { refresh = false, html = null, cart = null } = {}) {
      if (opener instanceof HTMLElement) this.opener = opener;
      else if (!this.dialog.open) this.opener = document.activeElement;

      // One modal at a time: close quick-add sheets, menus, search.
      document.querySelectorAll('dialog[open]').forEach((dialog) => {
        if (dialog !== this.dialog) dialog.close();
      });

      if (html) this.form.sync({ html, cart });
      else if (refresh || this.form.stale) this.form.refresh(cart);
      if (this.dialog.open) return;
      this.dialog.showModal();
      this.triggers().forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
      const title = this.dialog.querySelector('#CartDrawer-Title');
      if (title) title.focus({ preventScroll: true });
    }

    close() {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
      if (motionOff()) return this.dialog.close();
      this.dialog.classList.add('is-closing');
      const panel = this.dialog.firstElementChild;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        this.dialog.close();
      };
      panel.addEventListener('animationend', finish, { once: true });
      setTimeout(finish, 420);
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const items = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.getClientRects().length > 0 && !el.closest('[inert]')
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement.id === 'CartDrawer-Title') {
        event.preventDefault();
        last.focus();
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ---------------------------------------------------------------------- */
  /* <cart-upsell>                                                          */
  /* ---------------------------------------------------------------------- */
  const recommendations = new Map();
  const isTaggedNew = (product) =>
    (Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(','))
      .some((tag) => String(tag).trim().toLowerCase() === 'new');

  class CartUpsell extends HTMLElement {
    connectedCallback() {
      this.load();
    }

    async fetchProducts(productId) {
      if (recommendations.has(productId)) return recommendations.get(productId);
      const base = this.dataset.url;
      const limit = (Number(this.dataset.limit) || 4) + 4;
      const promise = (async () => {
        for (const intent of ['complementary', 'related']) {
          try {
            const data = await request(`${base}.json?product_id=${productId}&limit=${limit}&intent=${intent}`);
            if (data.products && data.products.length) return data.products;
          } catch (error) {
            /* try the next intent */
          }
        }
        return [];
      })();
      recommendations.set(productId, promise);
      return promise;
    }

    async load() {
      const productId = this.dataset.productId;
      const list = this.querySelector('[data-upsell-list]');
      const template = this.querySelector('[data-upsell-template]');
      if (!productId || !list || !template) return this.remove();

      const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean).map(Number));
      const products = (await this.fetchProducts(productId))
        .filter((product) => product.available && !exclude.has(product.id))
        .slice(0, Number(this.dataset.limit) || 4);
      if (!this.isConnected) return;
      if (!products.length) {
        this.hidden = true;
        return;
      }

      const items = products.map((product) => this.card(template, product));
      list.replaceChildren(...items);
      list.removeAttribute('aria-busy');
      if (!motionOff()) {
        items.forEach((item, index) =>
          item.animate([{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], {
            duration: 380,
            delay: index * 60,
            easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
            fill: 'backwards',
          })
        );
      }
    }

    card(template, product) {
      const node = template.content.firstElementChild.cloneNode(true);
      const url = product.url || `${root}products/${product.handle}`;
      node.querySelectorAll('[data-u-link]').forEach((link) => link.setAttribute('href', url));
      node.querySelector('[data-u-title]').textContent = product.title;
      const newPill = node.querySelector('[data-u-new]');
      if (newPill && isTaggedNew(product)) newPill.hidden = false;

      const img = node.querySelector('[data-u-img]');
      const src = product.featured_image && typeof product.featured_image === 'object' ? product.featured_image.url : product.featured_image;
      if (src) {
        const imageUrl = new URL(src, window.location.href);
        imageUrl.searchParams.set('width', '240');
        img.src = imageUrl.toString();
      } else {
        img.remove();
      }

      const variants = (product.variants || []).filter((variant) => variant.available);
      const first = variants[0];
      const priceEl = node.querySelector('[data-u-price]');
      const compareEl = node.querySelector('[data-u-compare]');
      priceEl.textContent = money(first ? first.price : product.price);
      const compare = first ? first.compare_at_price : product.compare_at_price;
      if (compare && compare > (first ? first.price : product.price)) {
        compareEl.textContent = money(compare);
        compareEl.hidden = false;
      }

      const select = node.querySelector('[data-u-select]');
      const hidden = node.querySelector('[data-u-id]');
      const uid = `Upsell-${product.id}-${Math.random().toString(36).slice(2, 7)}`;
      if (variants.length > 1) {
        hidden.remove();
        select.id = uid;
        select.setAttribute('aria-label', (this.dataset.textChoose || '').replace('[title]', product.title));
        variants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.textContent = variant.title;
          option.dataset.price = money(variant.price);
          select.append(option);
        });
      } else {
        select.remove();
        hidden.value = first ? first.id : '';
      }
      node
        .querySelector('[data-u-add]')
        .setAttribute('aria-label', (this.dataset.textAdd || '').replace('[title]', product.title));
      return node;
    }
  }
  customElements.define('cart-upsell', CartUpsell);
})();
